import { useState, useEffect, useRef } from 'react'
import api from '../services/api'

interface Skill {
  id: string
  name: string
  description: string
  content: string
  tags: string[]
  category: string
  type: string
  source: string
  project_id?: string
  created_at: string
}

interface Project {
  id: string
  name: string
  icon: string
  color: string
  system_prompt: string
  created_at: string
  updated_at: string
}

interface SecurityIssue {
  category: string
  description: string
  severity: 'high' | 'medium' | 'low'
  matches: number
}

interface SecurityResult {
  safe: boolean
  risk_level: 'safe' | 'info' | 'warning' | 'danger'
  issues: SecurityIssue[]
  issues_count: number
}

interface UrlPreview {
  name: string
  description: string
  content: string
  tags: string[]
  category: string
  source: string
  security: SecurityResult
  size: number
}

const CATEGORY_ICONS: Record<string, string> = {
  'Infrastructure': '🖥️', 'Données': '🗄️', 'WordPress': '🌐', 'Obsidian': '🔮',
  'Musique': '🎸', 'Méthode': '📋', 'Général': '⚡', 'Code': '💻',
  'Écriture': '✍️', 'Analyse': '🔍', 'Assistant': '🤖', 'Créatif': '🎨',
  'Business': '💼', 'Metal/Musique': '🎸', 'Système': '⚙️',
}

const TYPE_LABELS: Record<string, string> = {
  prompt: '⚡ Prompts',
  conversation: '💬 Conversations',
  document: '📄 Documents',
  url: '🔗 URLs',
}

const TYPE_COLORS: Record<string, { color: string, bg: string, border: string }> = {
  prompt:       { color: '#a855f7', bg: '#1f2937', border: '#4c1d95' },
  conversation: { color: '#60a5fa', bg: '#1e3a5f', border: '#1d4ed8' },
  document:     { color: '#10b981', bg: '#064e3b', border: '#065f46' },
  url:          { color: '#f59e0b', bg: '#451a03', border: '#92400e' },
}

const RISK_CONFIG = {
  safe:    { color: '#10b981', bg: '#064e3b', border: '#10b981', icon: '✅', label: 'Sûr' },
  info:    { color: '#60a5fa', bg: '#1e3a5f', border: '#3b82f6', icon: 'ℹ️', label: 'Info' },
  warning: { color: '#f59e0b', bg: '#451a03', border: '#f59e0b', icon: '⚠️', label: 'Attention' },
  danger:  { color: '#ef4444', bg: '#7f1d1d', border: '#ef4444', icon: '🚨', label: 'Danger' },
}

const SEVERITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#60a5fa' }

const FILE_TYPES = [
  { icon: '📄', exts: '.md  .txt', label: 'Prompts, notes, conversations' },
  { icon: '📝', exts: '.docx', label: 'Documents Word / Google Docs exportés' },
  { icon: '📋', exts: '.json  .yaml', label: 'Skills structurés' },
  { icon: '🖼️', exts: '.png  .jpg  .webp', label: 'Images (stockage référence)' },
  { icon: '🎬', exts: '.mp4  .webm', label: 'Vidéos (stockage référence)' },
  { icon: '📁', exts: 'Copier-coller', label: 'Historique conversations Claude' },
]

export default function SkillsPage() {
  const [skills, setSkills] = useState<Skill[]>([])
  const [activeCategory, setActiveCategory] = useState('Tous')
  const [showForm, setShowForm] = useState(false)
  const [showUrlModal, setShowUrlModal] = useState(false)
  const [showPasteModal, setShowPasteModal] = useState(false)
  const [showFileTypes, setShowFileTypes] = useState(false)
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [urlLoading, setUrlLoading] = useState(false)
  const [message, setMessage] = useState<{text: string, type: 'success'|'error'} | null>(null)
  const [form, setForm] = useState({ name: '', description: '', content: '', tags: '', category: 'Infrastructure' })
  const [formSecurity, setFormSecurity] = useState<SecurityResult | null>(null)
  const [urlInput, setUrlInput] = useState('')
  const [urlPreview, setUrlPreview] = useState<UrlPreview | null>(null)
  const [readingSkill, setReadingSkill] = useState<Skill | null>(null)
  const [readEditMode, setReadEditMode] = useState(false)
  const [readEditContent, setReadEditContent] = useState('')
  const [savingReadSkill, setSavingReadSkill] = useState(false)
  const [pasteForm, setPasteForm] = useState({ name: '', content: '' })
  const [editingSkill, setEditingSkill] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const analyzeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { loadSkills() }, [])

  useEffect(() => {
    if (!form.content.trim()) { setFormSecurity(null); return }
    if (analyzeTimer.current) clearTimeout(analyzeTimer.current)
    analyzeTimer.current = setTimeout(() => runAnalysis(form.content), 800)
    return () => { if (analyzeTimer.current) clearTimeout(analyzeTimer.current) }
  }, [form.content])

  const loadSkills = async () => {
    try {
      const r = await api.get('/api/skills/')
      setSkills(r.data || [])
    } catch (e) { console.error(e) }
  }

  const showMsg = (text: string, type: 'success'|'error') => {
    setMessage({ text, type })
    setTimeout(() => setMessage(null), 4000)
  }

  const runAnalysis = async (content: string) => {
    if (!content.trim()) return
    setAnalyzing(true)
    try {
      const r = await api.post('/api/skills/analyze', { content })
      setFormSecurity(r.data)
    } catch (e) { console.error(e) }
    setAnalyzing(false)
  }

  const handleCreate = async () => {
    if (!form.name.trim()) return showMsg('Nom obligatoire', 'error')
    if (formSecurity?.risk_level === 'danger') return showMsg('Contenu dangereux détecté — impossible de sauvegarder', 'error')
    setLoading(true)
    try {
      await api.post('/api/skills/', {
        name: form.name,
        description: form.description,
        content: form.content,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        category: form.category,
        type: 'prompt',
      })
      setForm({ name: '', description: '', content: '', tags: '', category: 'Général' })
      setFormSecurity(null)
      setShowForm(false)
      showMsg('Skill créé ! 🎯', 'success')
      loadSkills()
    } catch (e) { showMsg('Erreur création', 'error') }
    setLoading(false)
  }

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await api.delete(`/api/skills/${id}`)
      showMsg('Skill supprimé', 'success')
      loadSkills()
    } catch (e) { showMsg('Erreur suppression', 'error') }
  }

  const handleUpdateSkill = async (id: string, category: string, type: string, project_id: string) => {
    try {
      await api.put(`/api/skills/${id}`, { category, type, project_id })
      showMsg('Skill mis à jour ✓', 'success')
      setEditingSkill(null)
      loadSkills()
    } catch (e) { showMsg('Erreur mise à jour', 'error') }
  }

  const openReadModal = (skill: Skill) => {
    setReadingSkill(skill)
    setReadEditMode(false)
    setReadEditContent(skill.content || '')
  }

  const handleSaveReadSkill = async () => {
    if (!readingSkill) return
    setSavingReadSkill(true)
    try {
      await api.put(`/api/skills/${readingSkill.id}`, { content: readEditContent })
      showMsg('Skill sauvegardé ✓', 'success')
      setReadingSkill({ ...readingSkill, content: readEditContent })
      setReadEditMode(false)
      loadSkills()
    } catch { showMsg('Erreur sauvegarde', 'error') }
    setSavingReadSkill(false)
  }

  const handleExport = (skill: Skill, e: React.MouseEvent) => {
    e.stopPropagation()
    const md = [
      `# ${skill.name}`,
      skill.description ? `\n${skill.description}` : '',
      skill.tags?.length ? `\n**Tags**: ${skill.tags.join(', ')}` : '',
      skill.category ? `**Catégorie**: ${skill.category}` : '',
      skill.source ? `**Source**: ${skill.source}` : '',
      `\n---\n`,
      skill.content || '',
    ].filter(Boolean).join('\n')
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${skill.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    let name = '', description = '', content = '', tags: string[] = []
    try {
      if (ext === 'docx') {
        const formData = new FormData()
        formData.append('file', file)
        const r = await api.post('/api/skills/extract-docx', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        content = r.data.content
        if (r.data.size >= 50000) showMsg('⚠️ Document tronqué à 50 000 caractères', 'error')
        const firstLine = content.split('\n').find((l: string) => l.trim())
        name = firstLine ? firstLine.trim().substring(0, 60) : file.name.replace(/\.[^.]+$/, '')
      } else {
        const text = await file.text()
        if (ext === 'json') {
          const j = JSON.parse(text)
          name = j.name || file.name
          description = j.description || ''
          tags = j.tags || []
          content = j.content || JSON.stringify(j, null, 2)
        } else if (ext === 'md') {
          const lines = text.split('\n')
          name = lines[0].replace(/^#+\s*/, '').trim() || file.name
          for (const line of lines.slice(1, 6)) {
            if (line.trim() && !line.startsWith('#')) { description = line.trim().substring(0, 200); break }
          }
          content = text
        } else {
          name = file.name.replace(/\.[^.]+$/, '')
          content = text
        }
      }
      setLoading(true)
      const secR = await api.post('/api/skills/analyze', { content })
      const sec: SecurityResult = secR.data
      // Les fichiers techniques (URLs, commandes) sont normaux — on n'impose pas de blocage
      // pour les documents : on catégorise en "Système" et on avertit sans bloquer
      const isSystemContent = sec.risk_level === 'danger'
      await api.post('/api/skills/', {
        name, description, content, tags,
        type: 'document',
        category: isSystemContent ? 'Système' : undefined
      })
      if (isSystemContent) {
        showMsg(`"${name}" importé ⚙️ — catégorie Système (${sec.issues_count} avert. sécurité ignoré${sec.issues_count > 1 ? 's' : ''})`, 'success')
      } else {
        showMsg(`"${name}" importé !${sec.issues_count > 0 ? ` ⚠️ ${sec.issues_count} avertissement(s)` : ' ✅'}`, 'success')
      }
      loadSkills()
    } catch (err) { showMsg('Erreur import', 'error') }
    setLoading(false)
    e.target.value = ''
  }

  const handlePasteSave = async () => {
    if (!pasteForm.name.trim()) return showMsg('Nom obligatoire', 'error')
    if (!pasteForm.content.trim()) return showMsg('Contenu obligatoire', 'error')
    setLoading(true)
    try {
      const secR = await api.post('/api/skills/analyze', { content: pasteForm.content })
      const sec: SecurityResult = secR.data
      const isSystemContent = sec.risk_level === 'danger'
      await api.post('/api/skills/', {
        name: pasteForm.name,
        description: 'Conversation importée',
        content: pasteForm.content,
        tags: ['conversation'],
        category: isSystemContent ? 'Système' : 'Assistant',
        type: 'conversation',
      })
      if (isSystemContent) {
        showMsg(`"${pasteForm.name}" sauvegardé ⚙️ — catégorie Système (${sec.issues_count} avert. ignoré${sec.issues_count > 1 ? 's' : ''})`, 'success')
      } else {
        showMsg(`"${pasteForm.name}" sauvegardé ! ✅`, 'success')
      }
      setPasteForm({ name: '', content: '' })
      setShowPasteModal(false)
      loadSkills()
    } catch (e) { showMsg('Erreur sauvegarde', 'error') }
    setLoading(false)
  }

  const handleUrlFetch = async () => {
    if (!urlInput.trim()) return
    setUrlLoading(true)
    setUrlPreview(null)
    try {
      const r = await api.post('/api/skills/import-url', { url: urlInput })
      setUrlPreview(r.data)
    } catch (err: any) {
      showMsg(err.response?.data?.detail || 'Erreur téléchargement URL', 'error')
    }
    setUrlLoading(false)
  }

  const handleUrlImport = async () => {
    if (!urlPreview) return
    const isSystemUrl = urlPreview.security.risk_level === 'danger'
    setLoading(true)
    try {
      await api.post('/api/skills/', {
        name: urlPreview.name,
        description: urlPreview.description,
        content: urlPreview.content,
        tags: urlPreview.tags,
        category: isSystemUrl ? 'Système' : urlPreview.category,
        source: urlPreview.source,
        type: 'url',
      })
      if (isSystemUrl) {
        showMsg(`"${urlPreview.name}" importé ⚙️ — catégorie Système (${urlPreview.security.issues_count} avert. ignoré${urlPreview.security.issues_count > 1 ? 's' : ''})`, 'success')
      } else {
        showMsg(`"${urlPreview.name}" importé depuis URL ! ✅`, 'success')
      }
      setShowUrlModal(false)
      setUrlInput('')
      setUrlPreview(null)
      loadSkills()
    } catch (e) { showMsg('Erreur import', 'error') }
    setLoading(false)
  }

  // Catégories dynamiques dérivées des skills réels
  const allCategories = ['Tous', ...Array.from(new Set(skills.map(s => s.category || 'Général'))).sort()]

  const filteredSkills = activeCategory === 'Tous'
    ? skills
    : skills.filter(s => (s.category || 'Général') === activeCategory)

  const categoryCounts = skills.reduce((acc, s) => {
    const cat = s.category || 'Général'
    acc[cat] = (acc[cat] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const isGroupedView = activeCategory === 'Tous'
  const presentCategories = allCategories.filter(c => c !== 'Tous' && skills.some(s => (s.category || 'Général') === c))

  return (
    <div style={{ padding: '24px', height: '100%', overflowY: 'auto', color: 'white' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px', background: 'linear-gradient(to right, #ec4899, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        Skills
      </h1>
      <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
        {skills.length} skill{skills.length !== 1 ? 's' : ''} — Gérez vos prompts réutilisables
      </p>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', background: message.type === 'success' ? '#064e3b' : '#7f1d1d', border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`, color: message.type === 'success' ? '#10b981' : '#ef4444' }}>
          {message.text}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <button onClick={() => setShowForm(!showForm)} style={{ padding: '9px 18px', borderRadius: '8px', background: 'linear-gradient(to right, #ec4899, #a855f7)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
          + Nouveau skill
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={loading} style={{ padding: '9px 18px', borderRadius: '8px', background: '#1f2937', color: 'white', border: '1px solid #374151', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
          ↑ Importer fichier
        </button>
        <button onClick={() => { setShowPasteModal(true); setPasteForm({ name: '', content: '' }) }} style={{ padding: '9px 18px', borderRadius: '8px', background: '#1f2937', color: 'white', border: '1px solid #374151', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
          📋 Coller conversation
        </button>
        <button onClick={() => { setShowUrlModal(true); setUrlPreview(null); setUrlInput('') }} style={{ padding: '9px 18px', borderRadius: '8px', background: '#1f2937', color: 'white', border: '1px solid #6366f1', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
          🔗 Importer URL
        </button>
        <button onClick={() => setShowFileTypes(!showFileTypes)} style={{ padding: '9px 18px', borderRadius: '8px', background: showFileTypes ? '#1e3a5f' : '#1f2937', color: showFileTypes ? '#60a5fa' : '#9ca3af', border: `1px solid ${showFileTypes ? '#3b82f6' : '#374151'}`, cursor: 'pointer', fontSize: '13px' }}>
          📂 Types acceptés
        </button>
        <input ref={fileRef} type="file" accept=".json,.yaml,.yml,.md,.txt,.docx,.png,.jpg,.webp,.mp4,.webm" style={{ display: 'none' }} onChange={handleFileImport} />
      </div>

      {/* File types legend */}
      {showFileTypes && (
        <div style={{ background: '#0f172a', border: '1px solid #1e3a5f', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {FILE_TYPES.map(ft => (
            <div key={ft.exts} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1f2937', borderRadius: '8px', padding: '6px 12px' }}>
              <span style={{ fontSize: '16px' }}>{ft.icon}</span>
              <div>
                <div style={{ fontSize: '11px', color: '#60a5fa', fontWeight: '600', fontFamily: 'monospace' }}>{ft.exts}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{ft.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: '#6b7280', alignSelf: 'center', marginRight: '4px' }}>CATÉGORIE</span>
        {allCategories.filter(c => c === 'Tous' || categoryCounts[c] > 0).map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)} style={{
            padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
            background: activeCategory === cat ? 'linear-gradient(to right, #ec4899, #a855f7)' : '#1f2937',
            color: 'white', border: activeCategory === cat ? 'none' : '1px solid #374151'
          }}>
            {cat !== 'Tous' && CATEGORY_ICONS[cat] && <span style={{ marginRight: '3px' }}>{CATEGORY_ICONS[cat]}</span>}
            {cat} <span style={{ opacity: 0.7, fontSize: '10px' }}>({cat === 'Tous' ? skills.length : (categoryCounts[cat] || 0)})</span>
          </button>
        ))}
      </div>

      {/* New skill form */}
      {showForm && (
        <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Nouveau skill</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input placeholder="Nom *" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              style={{ padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', boxSizing: 'border-box' as const }} />
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              style={{ padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', boxSizing: 'border-box' as const }}>
              {allCategories.filter(c => c !== 'Tous').map(c => <option key={c} value={c}>{CATEGORY_ICONS[c] || ''} {c}</option>)}
            </select>
          </div>
          <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
            style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', marginBottom: '12px', boxSizing: 'border-box' as const }} />
          <div style={{ position: 'relative' }}>
            <textarea placeholder="Contenu du skill / prompt..." value={form.content} onChange={e => setForm({...form, content: e.target.value})}
              rows={8} style={{ width: '100%', padding: '10px', background: '#1f2937', border: `1px solid ${formSecurity ? (RISK_CONFIG[formSecurity.risk_level]?.border || '#374151') : '#374151'}`, borderRadius: '8px', color: 'white', marginBottom: '8px', boxSizing: 'border-box' as const, resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }} />
            {analyzing && <div style={{ position: 'absolute', top: '8px', right: '10px', fontSize: '11px', color: '#9ca3af' }}>🔍 Analyse...</div>}
          </div>
          <SecurityBadge result={formSecurity} />
          <input placeholder="Tags (séparés par virgule)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})}
            style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', marginBottom: '16px', marginTop: '8px', boxSizing: 'border-box' as const }} />
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleCreate} disabled={loading || formSecurity?.risk_level === 'danger'}
              style={{ padding: '10px 24px', borderRadius: '8px', background: formSecurity?.risk_level === 'danger' ? '#374151' : 'linear-gradient(to right, #ec4899, #a855f7)', color: 'white', border: 'none', cursor: formSecurity?.risk_level === 'danger' ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
              {loading ? 'Création...' : 'Créer'}
            </button>
            <button onClick={() => { setShowForm(false); setFormSecurity(null) }} style={{ padding: '10px 24px', borderRadius: '8px', background: '#374151', color: 'white', border: 'none', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Paste conversation modal */}
      {showPasteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: '16px', padding: '28px', width: '640px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700' }}>📋 Coller une conversation</h3>
              <button onClick={() => setShowPasteModal(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '16px' }}>Collez directement le texte d'une conversation Claude, ChatGPT ou autre.</p>
            <input placeholder="Nom de la conversation *" value={pasteForm.name} onChange={e => setPasteForm({...pasteForm, name: e.target.value})}
              style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', marginBottom: '12px', boxSizing: 'border-box' as const, fontSize: '14px' }} />
            <textarea placeholder="Collez ici le contenu de la conversation..." value={pasteForm.content} onChange={e => setPasteForm({...pasteForm, content: e.target.value})}
              rows={12} style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', marginBottom: '16px', boxSizing: 'border-box' as const, resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }} />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handlePasteSave} disabled={loading}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', background: 'linear-gradient(to right, #ec4899, #a855f7)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
                {loading ? 'Sauvegarde...' : '💾 Sauvegarder'}
              </button>
              <button onClick={() => setShowPasteModal(false)} style={{ padding: '11px 20px', borderRadius: '8px', background: '#374151', color: 'white', border: 'none', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL import modal */}
      {showUrlModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#111827', border: '1px solid #374151', borderRadius: '16px', padding: '28px', width: '600px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700' }}>🔗 Importer depuis URL</h3>
              <button onClick={() => setShowUrlModal(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '16px' }}>Supporte .md, .json, .txt — GitHub Raw, Gist, etc.</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input value={urlInput} onChange={e => setUrlInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleUrlFetch()}
                placeholder="https://raw.githubusercontent.com/..." style={{ flex: 1, padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white', fontSize: '13px' }} />
              <button onClick={handleUrlFetch} disabled={urlLoading || !urlInput.trim()}
                style={{ padding: '10px 20px', borderRadius: '8px', background: '#6366f1', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                {urlLoading ? '⏳' : '🔍 Fetch'}
              </button>
            </div>
            {urlPreview && (
              <div>
                <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>{urlPreview.name}</div>
                  <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px' }}>{urlPreview.category} · {(urlPreview.size / 1024).toFixed(1)} Ko</div>
                  {urlPreview.description && <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '8px' }}>{urlPreview.description}</p>}
                  <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace', background: '#111827', padding: '8px', borderRadius: '6px', maxHeight: '100px', overflow: 'hidden' }}>
                    {urlPreview.content.substring(0, 300)}...
                  </div>
                </div>
                <SecurityBadge result={urlPreview.security} />
                <button onClick={handleUrlImport} disabled={loading || urlPreview.security.risk_level === 'danger'}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', background: urlPreview.security.risk_level === 'danger' ? '#374151' : 'linear-gradient(to right, #6366f1, #a855f7)', color: 'white', border: 'none', cursor: urlPreview.security.risk_level === 'danger' ? 'not-allowed' : 'pointer', fontWeight: '700', marginTop: '12px' }}>
                  {loading ? 'Import...' : urlPreview.security.risk_level === 'danger' ? '🚨 Bloqué' : '✅ Importer ce skill'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal lecture + édition skill */}
      {readingSkill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={() => { setReadingSkill(null); setReadEditMode(false) }}>
          <div style={{ background: '#111827', border: '1px solid #4b5563', borderRadius: '14px', width: '100%', maxWidth: '860px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#ec4899', margin: '0 0 4px 0' }}>{readingSkill.name}</h2>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {readingSkill.category && <span style={{ fontSize: '11px', padding: '2px 8px', background: '#ec489920', color: '#ec4899', borderRadius: '10px' }}>{CATEGORY_ICONS[readingSkill.category] || ''} {readingSkill.category}</span>}
                  {readingSkill.description && <span style={{ fontSize: '12px', color: '#9ca3af' }}>{readingSkill.description}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
                {!readEditMode ? (
                  <button onClick={() => { setReadEditMode(true); setReadEditContent(readingSkill.content || '') }}
                    style={{ background: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '5px 12px', fontSize: '13px' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ec4899'; e.currentTarget.style.color = '#ec4899' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#4b5563'; e.currentTarget.style.color = '#9ca3af' }}>
                    ✏️ Modifier
                  </button>
                ) : (
                  <>
                    <button onClick={handleSaveReadSkill} disabled={savingReadSkill}
                      style={{ background: '#10b981', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', padding: '5px 14px', fontSize: '13px', fontWeight: '600', opacity: savingReadSkill ? 0.6 : 1 }}>
                      {savingReadSkill ? 'Sauvegarde...' : '💾 Sauvegarder'}
                    </button>
                    <button onClick={() => setReadEditMode(false)}
                      style={{ background: '#374151', border: 'none', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '5px 10px', fontSize: '13px' }}>
                      Annuler
                    </button>
                  </>
                )}
                <button onClick={() => { setReadingSkill(null); setReadEditMode(false) }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '22px', padding: '2px', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {/* Contenu */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {readEditMode ? (
                <textarea
                  value={readEditContent}
                  onChange={e => setReadEditContent(e.target.value)}
                  style={{ width: '100%', minHeight: '400px', background: '#0f172a', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', color: '#d1d5db', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.7', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                />
              ) : (
                <pre style={{ background: '#0f172a', border: '1px solid #1f2937', borderRadius: '8px', padding: '16px', color: '#d1d5db', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {readingSkill.content || '— Aucun contenu —'}
                </pre>
              )}
            </div>
            {readingSkill.source && (
              <div style={{ padding: '10px 20px', borderTop: '1px solid #1f2937', fontSize: '11px', color: '#6b7280' }}>
                Source : <a href={readingSkill.source} target="_blank" rel="noreferrer" style={{ color: '#6366f1' }}>{readingSkill.source}</a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Skills display */}
      {skills.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>Aucun skill pour le moment</p>
          <p style={{ fontSize: '14px' }}>Créez ou importez votre premier skill</p>
        </div>
      ) : isGroupedView ? (
        // Grouped view: sections par catégorie
        presentCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p>Aucun skill dans cette sélection</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {presentCategories.map(cat => {
              const catSkills = skills.filter(s => (s.category || 'Général') === cat)
              return (
                <div key={cat}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #1f2937' }}>
                    <span style={{ fontSize: '20px' }}>{CATEGORY_ICONS[cat] || '⚡'}</span>
                    <h2 style={{ fontSize: '17px', fontWeight: '700', color: '#f9fafb' }}>{cat}</h2>
                    <span style={{ padding: '2px 10px', background: '#1f2937', borderRadius: '12px', fontSize: '12px', color: '#a855f7', border: '1px solid #4c1d95', fontWeight: '600' }}>
                      {catSkills.length}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
                    {catSkills.map(skill => (
                      <SkillCard key={skill.id} skill={skill}
                        onDelete={handleDelete} onExport={handleExport}
                        editing={editingSkill === skill.id}
                        onEditToggle={(e) => { e.stopPropagation(); setEditingSkill(editingSkill === skill.id ? null : skill.id) }}
                        onUpdate={handleUpdateSkill}
                        onRead={(e) => { e.stopPropagation(); openReadModal(skill) }} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )
      ) : filteredSkills.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>Aucun skill dans cette sélection</p>
        </div>
      ) : (
        // Flat filtered grid
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {filteredSkills.map(skill => (
            <SkillCard key={skill.id} skill={skill} projects={projects}
              onDelete={handleDelete} onExport={handleExport}
              editing={editingSkill === skill.id}
              onEditToggle={(e) => { e.stopPropagation(); setEditingSkill(editingSkill === skill.id ? null : skill.id) }}
              onUpdate={handleUpdateSkill}
              onRead={(e) => { e.stopPropagation(); openReadModal(skill) }} />
          ))}
        </div>
      )}
    </div>
  )
}

function SkillCard({ skill, onDelete, onExport, editing, onEditToggle, onUpdate, onRead }: {
  skill: Skill
  onDelete: (id: string, e: React.MouseEvent) => void
  onExport: (skill: Skill, e: React.MouseEvent) => void
  editing: boolean
  onEditToggle: (e: React.MouseEvent) => void
  onUpdate: (id: string, category: string, type: string, project_id: string) => void
  onRead: (e: React.MouseEvent) => void
}) {
  const [editCategory, setEditCategory] = useState(skill.category || 'Infrastructure')
  const [editType, setEditType] = useState(skill.type || 'prompt')
  const allCats = Object.keys(CATEGORY_ICONS)

  return (
    <div style={{ background: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', transition: 'border-color 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#ec4899'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#4b5563'}>

      {/* Titre + boutons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff', margin: 0, lineHeight: '1.4' }}>{skill.name}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button onClick={onRead}
            style={{ background: '#374151', border: '1px solid #4b5563', borderRadius: '6px', color: '#d1d5db', cursor: 'pointer', padding: '4px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#ec489920'; e.currentTarget.style.borderColor = '#ec4899'; e.currentTarget.style.color = '#ec4899' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#374151'; e.currentTarget.style.borderColor = '#4b5563'; e.currentTarget.style.color = '#d1d5db' }}>
            📖 Lire
          </button>
          <button onClick={e => onExport(skill, e)} title="Exporter .md"
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '14px', padding: '4px', borderRadius: '4px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#d1d5db'}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}>⬇️</button>
          <button onClick={e => onDelete(skill.id, e)} title="Supprimer"
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '4px', borderRadius: '4px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}>×</button>
        </div>
      </div>

      {/* Description */}
      {skill.description && (
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 10px 0', lineHeight: '1.5' }}>{skill.description}</p>
      )}

      {/* Badges + bouton config */}
      {!editing ? (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {skill.category && (
            <span style={{ background: '#ec489920', color: '#ec4899', fontSize: '12px', padding: '3px 10px', borderRadius: '4px' }}>
              {CATEGORY_ICONS[skill.category] || ''} {skill.category}
            </span>
          )}
          {skill.type && skill.type !== 'prompt' && (
            <span style={{ background: (TYPE_COLORS[skill.type] || TYPE_COLORS.prompt).bg, color: (TYPE_COLORS[skill.type] || TYPE_COLORS.prompt).color, fontSize: '11px', padding: '3px 8px', borderRadius: '4px', border: `1px solid ${(TYPE_COLORS[skill.type] || TYPE_COLORS.prompt).border}` }}>
              {TYPE_LABELS[skill.type] || skill.type}
            </span>
          )}
          {skill.tags?.map(tag => (
            <span key={tag} style={{ background: '#111827', color: '#6b7280', fontSize: '11px', padding: '2px 6px', borderRadius: '4px' }}>#{tag}</span>
          ))}
          <button onClick={onEditToggle}
            style={{ marginLeft: 'auto', background: 'none', border: '1px solid #374151', borderRadius: '6px', color: '#6b7280', cursor: 'pointer', padding: '2px 8px', fontSize: '11px' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.color = '#a855f7' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.color = '#6b7280' }}>
            ⚙️ Config
          </button>
        </div>
      ) : (
        <div onClick={e => e.stopPropagation()} style={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px', padding: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#9ca3af', display: 'block', marginBottom: '4px' }}>Catégorie</label>
              <select value={editCategory} onChange={e => setEditCategory(e.target.value)}
                style={{ width: '100%', padding: '6px', background: '#1f2937', border: '1px solid #374151', borderRadius: '6px', color: 'white', fontSize: '12px' }}>
                {allCats.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#9ca3af', display: 'block', marginBottom: '4px' }}>Type</label>
              <select value={editType} onChange={e => setEditType(e.target.value)}
                style={{ width: '100%', padding: '6px', background: '#1f2937', border: '1px solid #374151', borderRadius: '6px', color: 'white', fontSize: '12px' }}>
                <option value="prompt">⚡ Prompt</option>
                <option value="conversation">💬 Conversation</option>
                <option value="document">📄 Document</option>
                <option value="url">🔗 URL</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={() => onUpdate(skill.id, editCategory, editType, '')}
              style={{ flex: 1, padding: '6px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              ✓ Enregistrer
            </button>
            <button onClick={onEditToggle}
              style={{ padding: '6px 12px', background: '#374151', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function SecurityBadge({ result }: { result: SecurityResult | null }) {
  if (!result) return null
  const cfg = RISK_CONFIG[result.risk_level]
  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: result.issues.length > 0 ? '8px' : '0' }}>
        <span>{cfg.icon}</span>
        <span style={{ color: cfg.color, fontWeight: '600', fontSize: '13px' }}>
          Analyse sécurité : {cfg.label}{result.issues_count > 0 ? ` — ${result.issues_count} problème(s)` : ''}
        </span>
      </div>
      {result.issues.map((issue, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
          <span style={{ color: SEVERITY_COLORS[issue.severity], fontWeight: '600', whiteSpace: 'nowrap' }}>[{issue.severity.toUpperCase()}]</span>
          <span>{issue.description}</span>
        </div>
      ))}
    </div>
  )
}
